import React, { useState } from 'react';
import { CalendarIcon, ClockIcon, ArrowRightIcon, BellIcon, BookmarkIcon, ShareIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, FileTextIcon, StarIcon, TagIcon, MessageCircleIcon, BotIcon, XIcon, SendIcon, EyeIcon, DownloadIcon, PrinterIcon } from 'lucide-react';
interface SearchResultsProps {
  onSelectionChange: (count: number) => void;
  onPolicySelect: (policy: any) => void;
  onPayerSelect: (payer: any) => void;
}
export function SearchResults({
  onSelectionChange = () => {},
  onPolicySelect = () => {},
  onPayerSelect = () => {}
}: SearchResultsProps) {
  // State for card selection
  const [selectedCards, setSelectedCards] = useState<number[]>([]);
  const [lastSelectedCard, setLastSelectedCard] = useState<number | null>(null);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const [activeAiCard, setActiveAiCard] = useState<number | null>(null);
  const [aiMessages, setAiMessages] = useState<Record<number, {
    role: string;
    content: string;
  }[]>>({});
  const [aiInput, setAiInput] = useState<string>('');
  const [isAiTyping, setIsAiTyping] = useState<boolean>(false);
  // Toggle card selection
  const toggleCardSelection = (id: number, event: React.MouseEvent) => {
    // Check if the click was on the title (which should open the modal)
    const target = event.target as HTMLElement;
    const isTitleClick = target.tagName === 'H3' || target.closest('h3') !== null || target.classList.contains('policy-title');
    // Check if the click was on the payer information
    const isPayerClick = target.closest('.payer-info') !== null || target.classList.contains('payer-logo') || target.classList.contains('payer-name') || target.classList.contains('payer-type');
    // Check if the click was on the AI button or AI panel
    const isAiClick = target.closest('.ai-button') !== null || target.closest('.ai-panel') !== null;
    if (isTitleClick) {
      // Find the selected policy
      const policy = results.find(result => result.id === id);
      if (policy) {
        onPolicySelect(policy);
      }
      return;
    }
    if (isPayerClick) {
      // Find the selected payer
      const policy = results.find(result => result.id === id);
      if (policy) {
        onPayerSelect(policy);
      }
      return;
    }
    if (isAiClick) {
      return;
    }
    setLastSelectedCard(id);
    const newSelectedCards = selectedCards.includes(id) ? selectedCards.filter(cardId => cardId !== id) : [...selectedCards, id];
    setSelectedCards(newSelectedCards);
    onSelectionChange(newSelectedCards.length);
  };
  // Select/deselect all cards
  const selectAllCards = () => {
    const newSelectedCards = selectedCards.length === results.length ? [] : results.map(result => result.id);
    setSelectedCards(newSelectedCards);
    onSelectionChange(newSelectedCards.length);
  };
  // Handle card hover
  const handleCardHover = (id: number | null) => {
    setHoveredCard(id);
  };
  // Handle AI assistant toggle
  const toggleAiAssistant = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (activeAiCard === id) {
      setActiveAiCard(null);
    } else {
      setActiveAiCard(id);
      // Initialize AI conversation if it doesn't exist
      if (!aiMessages[id]) {
        const policy = results.find(result => result.id === id);
        setAiMessages({
          ...aiMessages,
          [id]: [{
            role: 'assistant',
            content: `Hello! I'm MPI, your Medical Policy Intelligence assistant. I can help answer questions about ${policy?.title}. What would you like to know?`
          }]
        });
      }
    }
  };
  // Handle AI message input
  const handleAiInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAiInput(e.target.value);
  };
  // Handle AI message submission
  const handleAiSubmit = (id: number, e?: React.FormEvent) => {
    e?.preventDefault();
    if (!aiInput.trim()) return;
    const policy = results.find(result => result.id === id);
    const currentMessages = aiMessages[id] || [];
    // Add user message
    const updatedMessages = [...currentMessages, {
      role: 'user',
      content: aiInput
    }];
    setAiMessages({
      ...aiMessages,
      [id]: updatedMessages
    });
    setAiInput('');
    setIsAiTyping(true);
    // Simulate AI response after a delay
    setTimeout(() => {
      let response = `Based on ${policy?.payer}'s policy for ${policy?.title}, `;
      if (aiInput.toLowerCase().includes('coverage')) {
        response += 'coverage is determined by medical necessity criteria. The policy outlines specific conditions and clinical scenarios where this treatment is considered medically necessary.';
      } else if (aiInput.toLowerCase().includes('criteria') || aiInput.toLowerCase().includes('requirement')) {
        response += 'key criteria include diagnosis confirmation by a specialist, documented failure of conservative treatments, and specific anatomical considerations.';
      } else if (aiInput.toLowerCase().includes('code') || aiInput.toLowerCase().includes('billing')) {
        response += 'this procedure is typically billed using CPT codes 30468, C1889, or L8699, depending on the specific application and anatomical location.';
      } else if (aiInput.toLowerCase().includes('update') || aiInput.toLowerCase().includes('change')) {
        response += `this policy was last updated on ${policy?.updated} and includes revised criteria for patient selection and treatment protocols.`;
      } else {
        response += "I can provide information about coverage criteria, medical necessity requirements, coding guidelines, and recent policy updates. Please let me know what specific aspect you'd like to learn more about.";
      }
      setAiMessages({
        ...aiMessages,
        [id]: [...updatedMessages, {
          role: 'assistant',
          content: response
        }]
      });
      setIsAiTyping(false);
    }, 1500);
  };
  // Randomized dummy data with diverse policy types and chronologically correct dates
  const results = [{
    id: 1,
    title: 'Radiofrequency Ablation of Miscellaneous Solid Tumors Excluding Liver Tumors',
    updated: '04/10/2024',
    posted: '05/23/2024',
    future: '05/23/2025',
    status: 'Active',
    category: 'Surgery',
    policyType: 'Medical',
    payerType: 'Commercial, Medicare Advantage',
    payer: 'Aetna',
    payerLogo: 'https://logo.clearbit.com/aetna.com'
  }, {
    id: 2,
    title: 'Radiofrequency Ablation of Malignant Lesions',
    updated: '01/15/2024',
    posted: '02/14/2024',
    future: '02/14/2025',
    status: 'Pending',
    category: 'Oncology',
    policyType: 'Payment',
    payerType: 'Commercial, Medicaid',
    payer: 'UnitedHealthcare',
    payerLogo: 'https://logo.clearbit.com/uhc.com'
  }, {
    id: 3,
    title: 'Radiofrequency Neurotomy for Chronic Pain Management',
    updated: '07/12/2024',
    posted: '08/07/2024',
    future: '08/07/2025',
    status: 'New',
    category: 'Pain Management',
    policyType: 'Reimbursement',
    payerType: 'Medicare Advantage',
    payer: 'Cigna',
    payerLogo: 'https://logo.clearbit.com/cigna.com'
  }, {
    id: 4,
    title: "Radiofrequency Ablation for Barrett's Esophagus",
    updated: '10/25/2023',
    posted: '11/30/2023',
    future: '11/30/2024',
    status: 'Archived',
    category: 'Gastroenterology',
    policyType: 'Coding',
    payerType: 'Commercial',
    payer: 'Humana',
    payerLogo: 'https://logo.clearbit.com/humana.com'
  }, {
    id: 5,
    title: 'Radiofrequency Ablation for Varicose Veins Treatment',
    updated: '03/05/2024',
    posted: '04/12/2024',
    future: '04/12/2025',
    status: 'Active',
    category: 'Vascular',
    policyType: 'Provider Bulletin',
    payerType: 'Commercial, Medicare Advantage',
    payer: 'Anthem Blue Cross',
    payerLogo: 'https://logo.clearbit.com/anthem.com'
  }, {
    id: 6,
    title: 'Radiofrequency Ablation for Thyroid Nodules',
    updated: '06/22/2024',
    posted: '07/19/2024',
    future: '07/19/2025',
    status: 'Draft',
    category: 'Endocrinology',
    policyType: 'Fee Schedule',
    payerType: 'Commercial',
    payer: 'Kaiser Permanente',
    payerLogo: 'https://logo.clearbit.com/kp.org'
  }, {
    id: 7,
    title: 'Radiofrequency Ablation for Renal Cell Carcinoma',
    updated: '12/10/2023',
    posted: '01/05/2024',
    future: '01/05/2025',
    status: 'Active',
    category: 'Urology',
    policyType: 'Medical',
    payerType: 'Medicare Advantage, Medicaid',
    payer: 'Molina Healthcare',
    payerLogo: 'https://logo.clearbit.com/molinahealthcare.com'
  }];
  const Pagination = () => <nav className="flex items-center" data-id="element-430">
      <button className="p-0.5 mr-0.5 text-gray-400" data-id="element-431">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-432">
          <path d="M15 18l-6-6 6-6" data-id="element-433" />
        </svg>
      </button>
      <button className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white flex items-center justify-center text-sm font-medium" data-id="element-434">
        1
      </button>
      <button className="w-8 h-8 rounded-full text-gray-600 flex items-center justify-center text-sm mx-0.5 hover:bg-gray-100" data-id="element-435">
        2
      </button>
      <button className="w-8 h-8 rounded-full text-gray-600 flex items-center justify-center text-sm mx-0.5 hover:bg-gray-100" data-id="element-436">
        3
      </button>
      <button className="w-8 h-8 rounded-full text-gray-600 flex items-center justify-center text-sm mx-0.5 hover:bg-gray-100" data-id="element-437">
        4
      </button>
      <button className="w-8 h-8 rounded-full text-gray-600 flex items-center justify-center text-sm mx-0.5 hover:bg-gray-100" data-id="element-438">
        5
      </button>
      <button className="p-0.5 ml-0.5 text-gray-400" data-id="element-439">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-440">
          <path d="M9 18l6-6-6-6" data-id="element-441" />
        </svg>
      </button>
    </nav>;
  // Get status color with vivid gradient colors at 5% opacity
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return {
          bg: 'bg-gradient-to-r from-green-500/5 to-emerald-500/5',
          bgHover: 'bg-gradient-to-r from-green-500/50 to-emerald-500/50',
          text: 'text-green-600',
          icon: <CheckCircleIcon size={16} className="mr-1.5 text-green-600" data-id="element-442" />,
          badge: 'bg-green-100 text-green-800 border-green-200'
        };
      case 'Draft':
        return {
          bg: 'bg-gradient-to-r from-red-500/5 to-rose-500/5',
          bgHover: 'bg-gradient-to-r from-red-500/50 to-rose-500/50',
          text: 'text-red-600',
          icon: <ClockIcon size={16} className="mr-1.5 text-red-600" data-id="element-443" />,
          badge: 'bg-red-100 text-red-800 border-red-200'
        };
      case 'Pending':
        return {
          bg: 'bg-gradient-to-r from-yellow-400/5 to-amber-500/5',
          bgHover: 'bg-gradient-to-r from-yellow-400/50 to-amber-500/50',
          text: 'text-amber-600',
          icon: <ClockIcon size={16} className="mr-1.5 text-amber-600" data-id="element-444" />,
          badge: 'bg-yellow-100 text-yellow-800 border-yellow-200'
        };
      case 'New':
        return {
          bg: 'bg-gradient-to-r from-fuchsia-500/5 to-purple-600/5',
          bgHover: 'bg-gradient-to-r from-fuchsia-500/50 to-purple-600/50',
          text: 'text-purple-600',
          icon: <StarIcon size={16} className="mr-1.5 text-purple-600" data-id="element-445" />,
          badge: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200'
        };
      case 'Archived':
        return {
          bg: 'bg-gradient-to-r from-gray-400/5 to-gray-500/5',
          bgHover: 'bg-gradient-to-r from-gray-400/50 to-gray-500/50',
          text: 'text-gray-600',
          icon: <FileTextIcon size={16} className="mr-1.5 text-gray-600" data-id="element-446" />,
          badge: 'bg-gray-100 text-gray-800 border-gray-200'
        };
      default:
        return {
          bg: 'bg-gradient-to-r from-gray-400/5 to-gray-500/5',
          bgHover: 'bg-gradient-to-r from-gray-400/50 to-gray-500/50',
          text: 'text-gray-600',
          icon: <AlertCircleIcon size={16} className="mr-1.5 text-gray-600" data-id="element-447" />,
          badge: 'bg-gray-100 text-gray-800 border-gray-200'
        };
    }
  };
  // Get category color
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Surgery':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Oncology':
        return 'bg-rose-100 text-rose-800 border-rose-200';
      case 'Pain Management':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'Gastroenterology':
        return 'bg-teal-100 text-teal-800 border-teal-200';
      case 'Vascular':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Endocrinology':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'Urology':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  // Get policy type color
  const getPolicyTypeColor = (policyType: string) => {
    switch (policyType) {
      case 'Medical':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Payment':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'Reimbursement':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'Coding':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'Provider Bulletin':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Fee Schedule':
        return 'bg-teal-100 text-teal-800 border-teal-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  return <div className="p-6 bg-white" data-id="element-448">
      <div className="space-y-4" data-id="element-449">
        {results.map(result => {
        const statusStyle = getStatusColor(result.status);
        return <div key={result.id} className={`relative overflow-hidden rounded-xl shadow-sm transition-all duration-300 cursor-pointer 
                ${selectedCards.includes(result.id) ? 'ring-[0.5px] ring-purple-500 ring-offset-2' : 'hover:shadow-md border border-gray-200'}`} onClick={e => toggleCardSelection(result.id, e)} onMouseEnter={() => handleCardHover(result.id)} onMouseLeave={() => handleCardHover(null)} data-id="element-450">
              {/* Select/Deselect All Bubble */}
              {selectedCards.length > 0 && lastSelectedCard === result.id && <div className="absolute -top-3 right-10 bg-white border border-gray-200 rounded-full px-3 py-1 shadow-sm z-10" data-id="element-451">
                  <button className="text-xs text-purple-600 font-medium" onClick={e => {
              e.stopPropagation();
              selectAllCards();
            }} data-id="element-452">
                    {selectedCards.length === results.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>}
              <div className="flex flex-col md:flex-row" data-id="element-453">
                {/* Left column: Status */}
                <div className={`${hoveredCard === result.id ? statusStyle.bgHover : statusStyle.bg} w-full md:w-[120px] p-3 md:p-4 flex md:flex-col justify-between items-center md:items-start transition-colors duration-200`} data-id="element-454">
                  <div className={`flex items-center ${statusStyle.text} font-medium text-sm`} data-id="element-455">
                    {statusStyle.icon}
                    {result.status}
                  </div>
                  <div className="flex space-x-1 md:mt-auto" data-id="element-456">
                    {hoveredCard === result.id && <>
                        <button className="p-1.5 bg-white bg-opacity-50 rounded-full text-gray-700 hover:bg-opacity-70" onClick={e => e.stopPropagation()} title="Notify" data-id="element-457">
                          <BellIcon size={16} data-id="element-458" />
                        </button>
                        <button className="p-1.5 bg-white bg-opacity-50 rounded-full text-gray-700 hover:bg-opacity-70" onClick={e => e.stopPropagation()} title="Bookmark" data-id="element-459">
                          <BookmarkIcon size={16} data-id="element-460" />
                        </button>
                        <button className="p-1.5 bg-white bg-opacity-50 rounded-full text-gray-700 hover:bg-opacity-70" onClick={e => e.stopPropagation()} title="Share" data-id="element-461">
                          <ShareIcon size={16} data-id="element-462" />
                        </button>
                      </>}
                  </div>
                </div>
                {/* Right column: Policy details */}
                <div className="flex-1 p-4 md:p-5" data-id="element-463">
                  <div className="flex flex-col md:flex-row md:items-start" data-id="element-464">
                    {/* Main content */}
                    <div className="flex-1" data-id="element-465">
                      {/* Policy Title */}
                      <h3 className="text-lg font-semibold mb-2 text-gray-900 policy-title hover:text-blue-600 transition-colors cursor-pointer line-clamp-2" data-id="element-466">
                        {result.title}
                      </h3>
                      {/* Payer Information */}
                      <div className="flex items-center mb-3 payer-info cursor-pointer" onClick={e => {
                    e.stopPropagation();
                    onPayerSelect(result);
                  }} data-id="element-467">
                        <div className="w-8 h-8 rounded-full bg-gray-100 overflow-hidden flex-shrink-0 mr-2 border border-gray-200" data-id="element-468">
                          <img src={result.payerLogo} alt={result.payer} className="w-full h-full object-contain p-1 payer-logo" onError={e => {
                        const target = e.target as HTMLImageElement;
                        target.onerror = null;
                        target.src = 'https://uploadthingy.s3.us-west-1.amazonaws.com/8WzAsTXnYG6PbLoyjpzxJz/image.png';
                      }} data-id="element-469" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-470">
                          <div className="text-sm font-medium truncate payer-name hover:text-blue-600" data-id="element-471">
                            {result.payer}
                          </div>
                          <div className="text-xs text-gray-500 truncate payer-type" data-id="element-472">
                            {result.payerType}
                          </div>
                        </div>
                      </div>
                      {/* Timeline dates */}
                      <div className="flex flex-wrap mb-3 gap-4" data-id="element-473">
                        <div className="flex items-center" data-id="element-474">
                          <div className="w-2 h-2 rounded-full bg-blue-500 mr-2" data-id="element-475"></div>
                          <div data-id="element-476">
                            <div className="text-xs text-gray-500 mb-0.5" data-id="element-477">
                              Updated
                            </div>
                            <div className="text-sm font-medium" data-id="element-478">
                              {result.updated}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center" data-id="element-479">
                          <div className="w-2 h-2 rounded-full bg-purple-500 mr-2" data-id="element-480"></div>
                          <div data-id="element-481">
                            <div className="text-xs text-gray-500 mb-0.5" data-id="element-482">
                              Posted
                            </div>
                            <div className="text-sm font-medium" data-id="element-483">
                              {result.posted}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center" data-id="element-484">
                          <div className="w-2 h-2 rounded-full bg-emerald-500 mr-2" data-id="element-485"></div>
                          <div data-id="element-486">
                            <div className="text-xs text-gray-500 mb-0.5" data-id="element-487">
                              Future Review
                            </div>
                            <div className="text-sm font-medium" data-id="element-488">
                              {result.future}
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Tags Row */}
                      <div className="flex flex-wrap gap-2" data-id="element-489">
                        <span className={`text-xs px-3 py-1 rounded-full border ${getCategoryColor(result.category)}`} data-id="element-490">
                          {result.category}
                        </span>
                        <span className={`text-xs px-3 py-1 rounded-full border ${getPolicyTypeColor(result.policyType)}`} data-id="element-491">
                          {result.policyType}
                        </span>
                      </div>
                    </div>
                    {/* Action buttons column */}
                    <div className="mt-4 md:mt-0 md:ml-4 flex flex-col items-end" data-id="element-492">
                      {/* AI Assistant Button - Subtle until hover */}
                      <button onClick={e => toggleAiAssistant(result.id, e)} className={`ai-button flex items-center px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 
                        ${activeAiCard === result.id ? 'bg-indigo-600 text-white scale-100' : hoveredCard === result.id ? 'bg-indigo-100 text-indigo-700 scale-100' : 'bg-indigo-50 text-indigo-600 opacity-70 scale-95'}`} data-id="element-493">
                        <BotIcon size={16} className="mr-2" data-id="element-494" />
                        {activeAiCard === result.id ? 'Close MPI' : 'Ask MPI'}
                      </button>
                      {/* Hover action buttons - Now below the MPI button */}
                      <div className={`flex space-x-2 mt-3 transition-all duration-300 ${hoveredCard === result.id ? 'opacity-100' : 'opacity-0'}`} data-id="element-495">
                        <button className="p-2 bg-gray-100 rounded-full text-gray-700 hover:bg-gray-200" onClick={e => e.stopPropagation()} title="View" data-id="element-496">
                          <EyeIcon size={16} data-id="element-497" />
                        </button>
                        <button className="p-2 bg-gray-100 rounded-full text-gray-700 hover:bg-gray-200" onClick={e => e.stopPropagation()} title="Download" data-id="element-498">
                          <DownloadIcon size={16} data-id="element-499" />
                        </button>
                        <button className="p-2 bg-gray-100 rounded-full text-gray-700 hover:bg-gray-200" onClick={e => e.stopPropagation()} title="Print" data-id="element-500">
                          <PrinterIcon size={16} data-id="element-501" />
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* AI Assistant Panel with animation */}
                  <div className={`ai-panel mt-4 overflow-hidden transition-all duration-500 ease-in-out
                      ${activeAiCard === result.id ? 'max-h-[400px] opacity-100 border-t border-gray-200 pt-4' : 'max-h-0 opacity-0 pt-0 border-t-0'}`} onClick={e => e.stopPropagation()} style={{
                transitionProperty: 'max-height, opacity, padding, border'
              }} data-id="element-502">
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 transform transition-transform duration-500 ease-out" style={{
                  transform: activeAiCard === result.id ? 'translateY(0)' : 'translateY(-20px)'
                }} data-id="element-503">
                      <div className="flex items-center mb-3" data-id="element-504">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-center" data-id="element-505">
                          <BotIcon size={18} className="text-white" data-id="element-506" />
                        </div>
                        <div className="ml-2" data-id="element-507">
                          <h4 className="font-medium text-gray-900" data-id="element-508">
                            Medical Policy Intelligence
                          </h4>
                          <p className="text-xs text-gray-500" data-id="element-509">
                            Ask questions about this policy
                          </p>
                        </div>
                      </div>
                      <div className="max-h-[240px] overflow-y-auto mb-3 space-y-3 p-1" data-id="element-510">
                        {aiMessages[result.id]?.map((message, idx) => <div key={idx} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-511">
                            <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${message.role === 'user' ? 'bg-indigo-600 text-white rounded-br-none' : 'bg-white border border-gray-200 text-gray-700 rounded-bl-none'}`} data-id="element-512">
                              {message.content}
                            </div>
                          </div>)}
                        {isAiTyping && activeAiCard === result.id && <div className="flex justify-start" data-id="element-513">
                            <div className="bg-white border border-gray-200 text-gray-700 rounded-lg rounded-bl-none px-3 py-2 text-sm" data-id="element-514">
                              <span className="inline-flex items-center" data-id="element-515">
                                <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-pulse mr-1" data-id="element-516"></span>
                                <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-pulse delay-150 mr-1" data-id="element-517"></span>
                                <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-pulse delay-300" data-id="element-518"></span>
                              </span>
                            </div>
                          </div>}
                      </div>
                      <form onSubmit={e => handleAiSubmit(result.id, e)} className="flex items-center" data-id="element-519">
                        <input type="text" value={aiInput} onChange={handleAiInputChange} placeholder="Ask about coverage, criteria, codes..." className="flex-1 text-sm border border-gray-300 rounded-l-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-520" />
                        <button type="submit" className="bg-indigo-600 text-white rounded-r-lg px-3 py-2 hover:bg-indigo-700" data-id="element-521">
                          <SendIcon size={16} data-id="element-522" />
                        </button>
                      </form>
                      <div className="mt-3 text-center" data-id="element-523">
                        <p className="text-xs text-gray-500" data-id="element-524">
                          MPI provides insights based on policy data. Results
                          may vary.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>;
      })}
      </div>
      <div className="flex items-center justify-between mt-8" data-id="element-525">
        <div className="text-sm text-gray-500" data-id="element-526">
          Showing <span className="font-medium" data-id="element-527">{results.length}</span> results
        </div>
        <div data-id="element-528">
          <Pagination data-id="element-529" />
        </div>
      </div>
    </div>;
}